import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatDetail } from "@/lib/api";
import { startEmergentGoogleAuth } from "@/lib/emergentAuth";
import { AuthShell } from "@/components/AuthShell";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success("Welcome back!");
            if (!user.profile_complete) navigate("/onboarding/profile");
            else if (!user.vibe_complete) navigate("/onboarding/vibe");
            else navigate("/app/match");
        } catch (err) {
            toast.error(formatDetail(err));
        } finally {
            setLoading(false);
        }
    };

    const google = () => startEmergentGoogleAuth();

    return (
        <AuthShell>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 text-primary px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
                welcome back
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] font-display">
                Log <span className="font-fancy text-gradient-purple">back in</span>
                <span className="text-primary">.</span>
            </h1>
            <p className="mt-2 text-muted-foreground text-base">Your matches missed you 💜</p>

            <Button
                onClick={google}
                data-testid="google-login-btn"
                variant="outline"
                className="mt-8 h-14 w-full rounded-2xl text-base font-bold lift border-border shadow-sm"
            >
                <GoogleIcon className="mr-2 h-5 w-5" /> Continue with Google
            </Button>

            <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px flex-1 bg-border" />
                or with email
                <div className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={submit} className="space-y-4">
                <div>
                    <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
                    <Input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="login-email-input"
                        className="mt-2 h-14 rounded-2xl border-border focus-visible:ring-primary"
                        placeholder="you@vibes.com"
                    />
                </div>
                <div>
                    <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
                    <Input
                        id="password"
                        type="password"
                        required
                        minLength={6}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="login-password-input"
                        className="mt-2 h-14 rounded-2xl border-border focus-visible:ring-primary"
                        placeholder="••••••"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit-btn"
                    className="h-14 w-full rounded-2xl text-base font-bold lift shadow-lg shadow-primary/25"
                >
                    {loading ? "Logging in…" : (<>Log in <ArrowRight className="ml-2 h-5 w-5" /></>)}
                </Button>
            </form>

            <p className="mt-6 text-sm text-muted-foreground">
                New here?{" "}
                <Link to="/register" data-testid="switch-to-register" className="font-semibold text-primary hover:underline">
                    Create an account
                </Link>
            </p>
        </AuthShell>
    );
}

function GoogleIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.11A6.61 6.61 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
        </svg>
    );
}
